/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  GridColDef,
  GridPinnedRowsProp,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { LinearProgress, Stack, Typography } from "@mui/material";

import {
  PlanConsumptionAggregation,
  useInventoryContent,
} from "./inventoryContent";
import {
  PlanTableDependencies,
  usePlanTableDependencies,
} from "../dependencies";
import { LoadedContent } from "src/components/common/loading/loadedContent";
import { InventoryColumnHeader } from "./InventoryColumnHeader";
import { TitleColumnHeader } from "../common/TitleColumnHeader";
import { InventoryRowHeader } from "./InventoryRowHeader";
import { InventoryCell } from "./InventoryCell";
import { PlainCell, StyledCell } from "../common/Cell";
import { INVENTORY_UNIT } from "../common/inventoryTotals";
import { ConsumptionMaterialValue, Row } from "./types";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { usePeriodIndex } from "hooks/periodIndex";
import { useTheme } from "@mui/material";

import type { JSX } from "react";

export const InventoryTable = ({
  planId,
  apiRef,
}: {
  planId: number;
  apiRef: ReturnType<typeof useGridApiRef>;
}) => {
  const dependencies = usePlanTableDependencies(planId);

  return (
    <LoadedContent
      data={dependencies}
      waiting={<LinearProgress />}
      loading={<LinearProgress />}
      enableFullHeight
    >
      {(loadedDependencies) => (
        <Stack gap={2} height="100%">
          <LoadedInventoryTable
            dependencies={loadedDependencies}
            apiRef={apiRef}
          />
        </Stack>
      )}
    </LoadedContent>
  );
};

// Custom percentage cell that maintains blue styling with 1 decimal place
const PercentageCell = ({ percentage }: { percentage: number }) => {
  const theme = useTheme();
  const displayValue = `${percentage.toFixed(1)}%`;

  return (
    <StyledCell
      color={theme.palette.data.lightblue}
      value={displayValue}
      alpha={percentage / 100} // Scale from 0-100% to 0-1 for alpha
      hasError={false}
    />
  );
};

const getConsumptionMaterialsMaximums = ({
  inventoryFinal,
  inventoryInitial,
  obtainableRemaining,
  obtainableRequired,
  consumption,
}: PlanConsumptionAggregation): ConsumptionMaterialValue => {
  return {
    inventoryFinal: Math.max(...Object.values(inventoryFinal.materials)),
    inventoryInitial: Math.max(...Object.values(inventoryInitial.materials)),
    obtainableRemaining: Math.max(
      ...Object.values(obtainableRemaining.materials)
    ),
    obtainableRequired: Math.max(
      ...Object.values(obtainableRequired.materials)
    ),
    consumption: Math.max(...Object.values(consumption.materials)),
  };
};

const LoadedInventoryTable = ({
  dependencies,
  apiRef,
}: {
  dependencies: PlanTableDependencies;
  apiRef: ReturnType<typeof useGridApiRef>;
}) => {
  const { t } = useTenantTranslation();
  const unit = useUnitsFormatter(true);
  const periodTab = usePeriodIndex();
  const consumption = useInventoryContent(
    dependencies.materials,
    dependencies.plan.summary.period_material_summary.filter(
      (summary) => summary.period === periodTab
    )
  );

  const scrapColumn: GridColDef<Row> = {
    field: "scrap",
    sortable: false,
    minWidth: 200,
    cellClassName: "group--header",
    display: "flex",
    headerName: t("scrap"),
    renderHeader: () => <TitleColumnHeader title={t("scrap")} />,
    valueGetter: (_, { scrap }) => {
      const { type } = scrap;
      switch (type) {
        case "aggregation": {
          const { variant } = scrap;
          switch (variant) {
            case "mass":
              return `${t("totalMass")}${unit(INVENTORY_UNIT)}`;
            default:
              return `${t("totalMass")}${unit(INVENTORY_UNIT)}`;
          }
        }
        case "material": {
          return scrap.material.name;
        }
      }
    },
    renderCell: ({ row: { scrap } }) => {
      const props = {
        ...scrap,
        period: periodTab,
      };
      return <InventoryRowHeader {...props} />;
    },
  };

  const inventoryKeys: (keyof PlanConsumptionAggregation)[] = [
    "inventoryInitial",
    "consumption",
    "inventoryFinal",
    "obtainableRequired",
    "obtainableRemaining",
  ];

  const inventoryColumns: GridColDef<Row>[] = inventoryKeys.map(
    (columnKey) => ({
      field: columnKey,
      headerName: t(columnKey),
      renderHeader: () => {
        return (
          <InventoryColumnHeader
            name={columnKey}
            planConsumptionType={columnKey}
          />
        );
      },
      valueGetter: (_, { data }) => {
        const value = data[columnKey];
        if (value) {
          return value;
        } else {
          return null;
        }
      },
      renderCell: ({ row: { data, scrap, maximums } }): JSX.Element | null => {
        const value = data[columnKey];
        const maximum = maximums[columnKey];

        if (value != null && maximum != null) {
          switch (scrap.type) {
            case "material": {
              return (
                <InventoryCell
                  type="material"
                  value={value}
                  maximum={maximum}
                  aggregation={columnKey}
                />
              );
            }
            case "aggregation": {
              return (
                <InventoryCell
                  type="aggregation"
                  value={value}
                  variant={scrap.variant}
                />
              );
            }
          }
        } else {
          return null;
        }
      },
      flex: 1,
    })
  );

  const consumptionPercentageColumn: GridColDef<Row> = {
    field: "consumptionPercentage",
    headerName: t("consumption") + " (%)",
    renderHeader: () => {
      return (
        <Typography
          sx={{
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
          variant="body2"
        >
          {`${t("consumption")} (%)`}
        </Typography>
      );
    },
    valueGetter: (_, { data }) => {
      const consumptionValue = data.consumption;
      const totalConsumption = consumption.consumption.total;

      if (
        consumptionValue != null &&
        totalConsumption != null &&
        totalConsumption > 0
      ) {
        const percentage = (consumptionValue / totalConsumption) * 100;
        return `${percentage.toFixed(1)}%`;
      }
      return null;
    },
    renderCell: ({ row: { data, scrap } }): JSX.Element | null => {
      const consumptionValue = data.consumption;
      const totalConsumption = consumption.consumption.total;

      if (
        consumptionValue != null &&
        totalConsumption != null &&
        totalConsumption > 0
      ) {
        const percentage = (consumptionValue / totalConsumption) * 100;
        const displayValue = `${percentage.toFixed(1)}%`;

        switch (scrap.type) {
          case "material": {
            return <PercentageCell percentage={percentage} />;
          }
          case "aggregation": {
            return <PlainCell value={displayValue} />;
          }
        }
      }
      return null;
    },
    flex: 1,
  };

  const consumptionIndex = inventoryColumns.findIndex(
    (col) => col.field === "consumption"
  );
  const allInventoryColumns = [
    ...inventoryColumns.slice(0, consumptionIndex + 1),
    consumptionPercentageColumn,
    ...inventoryColumns.slice(consumptionIndex + 1),
  ];

  const maximums = getConsumptionMaterialsMaximums(consumption);

  const rows: Row[] = dependencies.materials.byIndex.map((material, index) => {
    return {
      id: index,
      scrap: { type: "material", material },
      maximums,
      data: {
        inventoryInitial: consumption.inventoryInitial.materials[material.id],
        consumption: consumption.consumption.materials[material.id],
        inventoryFinal: consumption.inventoryFinal.materials[material.id],
        obtainableRequired:
          consumption.obtainableRequired.materials[material.id],
        obtainableRemaining:
          consumption.obtainableRemaining.materials[material.id],
      },
    };
  });

  const pinnedRows: GridPinnedRowsProp<Row> = {
    bottom: [
      {
        id: rows.length,
        maximums,
        scrap: { type: "aggregation", variant: "mass" },
        data: {
          inventoryInitial: consumption.inventoryInitial.total,
          consumption: consumption.consumption.total,
          inventoryFinal: consumption.inventoryFinal.total,
          obtainableRequired: consumption.obtainableRequired.total,
          obtainableRemaining: consumption.obtainableRemaining.total,
        },
      },
    ],
  };

  return (
    <HotkeyCompliantDataGrid
      sx={{
        "& .MuiDataGrid-cell": {
          paddingLeft: 0,
          paddingRight: 0,
        },
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
          whiteSpace: "normal",
        },
        "& .group--header": {
          py: 0.5,
          px: 1,
        },
      }}
      apiRef={apiRef}
      columns={[scrapColumn, ...allInventoryColumns]}
      rows={rows}
      pinnedRows={pinnedRows}
      cellSelection
      ignoreValueFormatterDuringExport
    />
  );
};
